import React from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { SUBSCRIPTION_PLANS } from '../../constants/subscriptionPlans';
import { SubscriptionPlan } from '../../types/subscription';
import { Subscription } from '../../types/subscription';

interface PlanComparisonProps {
  currentPlanId?: string;
  onSelectPlan?: (planId: string) => void;
  onUpgrade?: (planId: string) => void;
  onSubscribe?: (planId: string) => void;
  className?: string;
  showCurrentPlan?: boolean;
}

interface PlanCardProps {
  plan: SubscriptionPlan;
  isCurrentPlan?: boolean;
  isPopular?: boolean;
  onSelect?: () => void;
  onUpgrade?: () => void;
  onSubscribe?: () => void;
  className?: string;
}

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  isCurrentPlan = false,
  isPopular = false,
  onSelect,
  onUpgrade,
  onSubscribe,
  className = ''
}) => {
  const { language, strings } = useLocalization();
  const getPlanIcon = () => {
    switch (plan.icon) {
      case 'gift': return '🎁';
      case 'star': return '⭐';
      case 'crown': return '👑';
      default: return '📋';
    }
  };

  const getPlanColor = () => {
    switch (plan.color) {
      case 'gray': return 'border-gray-200';
      case 'blue': return 'border-blue-200';
      case 'gold': return 'border-yellow-200';
      default: return 'border-gray-200';
    }
  };

  const getButtonColor = () => {
    if (isCurrentPlan) return 'bg-gray-100 text-gray-500 cursor-not-allowed';
    if (plan.isFreeTier) return 'bg-primary hover:bg-primary-dark text-white';
    return 'bg-primary hover:bg-primary-dark text-white';
  };

  const getButtonText = () => {
    if (isCurrentPlan) return strings.subscriptions.currentPlan;
    if (plan.isFreeTier) return language === 'ar' ? 'اشترك الآن' : 'Subscribe Now';
    return language === 'ar' ? 'اشترك الآن' : 'Subscribe Now';
  };

  const formatLimit = (limit: number) => {
    return limit === -1 ? strings.subscriptions.unlimited : limit.toString();
  };

  return (
    <div className={`relative bg-white rounded-lg border-2 ${getPlanColor()} ${isPopular ? 'ring-2 ring-accent' : ''} ${className}`}>
      {/* Popular Badge */}
      {isPopular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-accent text-white text-xs font-medium px-3 py-1 rounded-full">
            {strings.subscriptions.mostPopular}
          </span>
        </div>
      )}

      {/* Current Plan Badge */}
      {isCurrentPlan && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-green-600 text-white text-xs font-medium px-3 py-1 rounded-full">
            {strings.subscriptions.currentPlan}
          </span>
        </div>
      )}

      {/* Header */}
      <div className="p-6 text-center">
        <div className="text-3xl mb-2">{getPlanIcon()}</div>
        <h3 className="text-xl font-semibold text-gray-900">{language === 'ar' ? plan.nameAr : plan.name}</h3>
        <p className="text-sm text-gray-600 mt-1">{language === 'ar' ? plan.descriptionAr : plan.description}</p>
        
        {/* Pricing */}
        <div className="mt-4">
          <div className="flex items-baseline justify-center">
            <span className="text-4xl font-bold text-gray-900">
              {plan.isFreeTier ? (language === 'ar' ? 'مجاني' : 'Free') : `${plan.price}`}
            </span>
            {!plan.isFreeTier && (
              <span className="text-lg text-gray-600 ml-1">EGP</span>
            )}
          </div>
          {!plan.isFreeTier && (
            <p className="text-sm text-gray-600">{language === 'ar' ? (plan.billingCycle === 'monthly' ? 'شهرياً' : 'سنوياً') : `per ${plan.billingCycle}`}</p>
          )}
        </div>
      </div>

      {/* Features */}
      <div className="px-6 pb-6">
        <div className="space-y-3">
          {/* Limits */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{strings.subscriptions.offices}:</span>
              <span className="font-medium">{formatLimit(plan.maxOffices)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{language === 'ar' ? `إجمالي ${strings.subscriptions.clients}` : `Total ${strings.subscriptions.clients}`}:</span>
              <span className="font-medium">{formatLimit(plan.maxClientsTotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{language === 'ar' ? `إجمالي ${strings.subscriptions.files}` : `Total ${strings.subscriptions.files}`}:</span>
              <span className="font-medium">{formatLimit(plan.maxFilesTotal)}</span>
            </div>
          </div>

          {/* Features */}
          <div className="pt-3 border-t border-gray-200">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <span className={`w-2 h-2 rounded-full ${plan.includesEventReminders ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className={`text-sm ${plan.includesEventReminders ? 'text-gray-700' : 'text-gray-400'}`}>
                  {strings.subscriptions.eventReminders}
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className={`w-2 h-2 rounded-full ${plan.includesAdvancedStats ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className={`text-sm ${plan.includesAdvancedStats ? 'text-gray-700' : 'text-gray-400'}`}>
                  {strings.subscriptions.advancedStats}
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className={`w-2 h-2 rounded-full ${plan.includesCommunity ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className={`text-sm ${plan.includesCommunity ? 'text-gray-700' : 'text-gray-400'}`}>
                  {strings.subscriptions.communityAccess}
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className={`w-2 h-2 rounded-full ${plan.includesPhoneSupport ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className={`text-sm ${plan.includesPhoneSupport ? 'text-gray-700' : 'text-gray-400'}`}>
                  {strings.subscriptions.phoneSupport}
                </span>
              </div>
            </div>
          </div>

          {/* Team Members */}
          {plan.teamMemberPrice && (
            <div className="pt-3 border-t border-gray-200">
              <div className="text-center">
                <p className="text-sm text-gray-600">{strings.subscriptions.teamMembers}</p>
                <p className="text-lg font-semibold text-gray-900">
                  {plan.teamMemberPrice} EGP {strings.subscriptions.perMember}
                </p>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Action Button */}
      <div className="px-6 pb-6">
        <button
          onClick={isCurrentPlan ? undefined : (onSubscribe || onUpgrade || onSelect)}
          disabled={isCurrentPlan}
          className={`w-full py-2 px-4 rounded-md font-medium transition-colors ${getButtonColor()}`}
        >
          {getButtonText()}
        </button>
      </div>
    </div>
  );
};

const PlanComparison: React.FC<PlanComparisonProps> = ({
  currentPlanId,
  onSelectPlan,
  onUpgrade,
  onSubscribe,
  className = '',
  showCurrentPlan = true
}) => {
  const { language, strings } = useLocalization();
  
  const handlePlanSelect = (planId: string) => {
    if (onSubscribe) {
      onSubscribe(planId);
    } else if (onSelectPlan) {
      onSelectPlan(planId);
    } else if (onUpgrade) {
      onUpgrade(planId);
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">{strings.subscriptions.planComparison}</h2>
        <p className="mt-2 text-gray-600">
          {language === 'ar' ? 'اختر الخطة التي تناسب احتياجات ممارستك القانونية' : 'Select the plan that best fits your law practice needs'}
        </p>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {SUBSCRIPTION_PLANS.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            isCurrentPlan={showCurrentPlan && currentPlanId === plan.id}
            isPopular={plan.popular}
            onSelect={() => handlePlanSelect(plan.id)}
            onUpgrade={() => onUpgrade?.(plan.id)}
            onSubscribe={() => onSubscribe?.(plan.id)}
          />
        ))}
      </div>

      {/* Features Comparison Table */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{language === 'ar' ? 'مقارنة المميزات' : 'Feature Comparison'}</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {language === 'ar' ? 'المميزات' : 'Features'}
                </th>
                {SUBSCRIPTION_PLANS.map((plan) => (
                  <th key={plan.id} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {language === 'ar' ? plan.nameAr : plan.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{strings.subscriptions.offices}</td>
                {SUBSCRIPTION_PLANS.map((plan) => (
                  <td key={plan.id} className="px-6 py-4 text-sm text-center text-gray-600">
                    {plan.maxOffices === -1 ? strings.subscriptions.unlimited : plan.maxOffices}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{language === 'ar' ? `إجمالي ${strings.subscriptions.clients}` : `Total ${strings.subscriptions.clients}`}</td>
                {SUBSCRIPTION_PLANS.map((plan) => (
                  <td key={plan.id} className="px-6 py-4 text-sm text-center text-gray-600">
                    {plan.maxClientsTotal === -1 ? strings.subscriptions.unlimited : plan.maxClientsTotal}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{language === 'ar' ? `إجمالي ${strings.subscriptions.files}` : `Total ${strings.subscriptions.files}`}</td>
                {SUBSCRIPTION_PLANS.map((plan) => (
                  <td key={plan.id} className="px-6 py-4 text-sm text-center text-gray-600">
                    {plan.maxFilesTotal === -1 ? strings.subscriptions.unlimited : plan.maxFilesTotal}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{strings.subscriptions.eventReminders}</td>
                {SUBSCRIPTION_PLANS.map((plan) => (
                  <td key={plan.id} className="px-6 py-4 text-sm text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      plan.includesEventReminders 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {plan.includesEventReminders ? '✓' : '✗'}
                    </span>
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{strings.subscriptions.advancedStats}</td>
                {SUBSCRIPTION_PLANS.map((plan) => (
                  <td key={plan.id} className="px-6 py-4 text-sm text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      plan.includesAdvancedStats 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {plan.includesAdvancedStats ? '✓' : '✗'}
                    </span>
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{strings.subscriptions.communityAccess}</td>
                {SUBSCRIPTION_PLANS.map((plan) => (
                  <td key={plan.id} className="px-6 py-4 text-sm text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      plan.includesCommunity 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {plan.includesCommunity ? '✓' : '✗'}
                    </span>
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{strings.subscriptions.phoneSupport}</td>
                {SUBSCRIPTION_PLANS.map((plan) => (
                  <td key={plan.id} className="px-6 py-4 text-sm text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      plan.includesPhoneSupport 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {plan.includesPhoneSupport ? '✓' : '✗'}
                    </span>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PlanComparison;
